import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { CHECKOUT_URL } from "@/data/plans";

const LINKS = [
  ["Challenges", "#challenges"],
  ["Rules", "#rules"],
  ["Mentorship", "#mentorship"],
  ["Max Capital", "#scaling"],
  ["FAQ", "#faq"],
];

export const Logo = () => (
  <Link to="/" data-testid="nav-logo" className="text-xl font-bold tracking-[-0.04em] text-white select-none">
    my<span className="gold-text">FX</span>Pocket
  </Link>
);

export const scrollToHash = (hash) => {
  const el = document.querySelector(hash);
  if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
  else if (el) el.scrollIntoView({ behavior: "smooth" });
};

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    if (pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToHash(href), 500);
    } else {
      scrollToHash(href);
    }
  };

  return (
    <header
      data-testid="header-navigation"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled || pathname !== "/" ? "bg-[#0D0E13]/85 backdrop-blur-xl border-b border-white/5 py-3" : "py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        <Logo />
        <nav className="hidden md:flex items-center gap-8">
          {LINKS.map(([label, href]) => (
            <a
              key={href}
              href={href}
              data-testid={`nav-link-${label.toLowerCase()}`}
              onClick={(e) => go(e, href)}
              className="text-sm font-medium text-[#DBDFE8] hover:text-[#FFD700] transition-colors duration-300"
            >
              {label}
            </a>
          ))}
          <Link
            to="/contact"
            data-testid="nav-link-contact"
            className={`text-sm font-medium transition-colors duration-300 ${
              pathname === "/contact" ? "text-[#FFD700]" : "text-[#DBDFE8] hover:text-[#FFD700]"
            }`}
          >
            Contact Us
          </Link>
          <a
            href={CHECKOUT_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-cta-button"
            className="btn-gold text-sm font-semibold rounded-full px-5 py-2.5"
          >
            Dashboard
          </a>
        </nav>
        <button
          data-testid="nav-mobile-menu-button"
          className="md:hidden text-white p-2"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {open && (
        <div className="md:hidden bg-[#0D0E13]/95 backdrop-blur-xl border-t border-white/5 px-6 py-6 flex flex-col gap-5">
          {LINKS.map(([label, href]) => (
            <a
              key={href}
              href={href}
              data-testid={`nav-mobile-link-${label.toLowerCase()}`}
              onClick={(e) => go(e, href)}
              className="text-base font-medium text-[#DBDFE8]"
            >
              {label}
            </a>
          ))}
          <Link
            to="/contact"
            data-testid="nav-mobile-link-contact"
            onClick={() => setOpen(false)}
            className="text-base font-medium text-[#DBDFE8]"
          >
            Contact Us
          </Link>
          <a
            href={CHECKOUT_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-mobile-cta-button"
            className="btn-gold text-sm font-semibold rounded-full px-5 py-3 text-center"
          >
            Dashboard
          </a>
        </div>
      )}
    </header>
  );
}
